/* @flow @format */

import { get } from 'lodash';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import F from 'lodash/fp';
import jwtDecode from 'jwt-decode';

import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';

import { tokenRefreshed } from '../redux/reducer/auth';
import REFRESH_AUTH from './mutation/RefreshAuth.gql';
import { store } from '../redux/store';

declare var API_ENDPOINT: string;

// Check wheter a token is expired or not
const isExpired = token => {
  try {
    // exp is timestamp
    const { exp } = jwtDecode(token);
    return Date.now() + 60000 > exp * 1000;
  } catch {
    return true;
  }
};

const authLink = setContext((request, { headers }) => {
  // advoid infinite loop
  const tokenUnneeded = F.includes(request.operationName, ['RefreshAuth', 'Collaboration', 'TicketSignin']);
  if (tokenUnneeded) return {};

  const state = store.getState();
  const refreshToken = get(state, 'auth.refreshToken');
  const accessToken = get(state, 'auth.accessToken');
  const resourceToken = get(state, 'auth.resourceToken');

  let tokenPromise = Promise.resolve([accessToken, resourceToken]);

  if ((isExpired(accessToken) || isExpired(resourceToken)) && refreshToken) {
    const { client } = require('./client');

    // refresh auth
    tokenPromise = client
      .mutate({
        mutation: REFRESH_AUTH,
        variables: { refreshToken },
      })
      .then(({ data }) => {
        if (!data) return Promise.reject();
        store.dispatch(tokenRefreshed(data.refreshAuth));
        const { accessToken, resourceToken } = data.refreshAuth;
        return [accessToken, resourceToken];
      });
  }

  // return the headers to the context so httpLink can read them
  return tokenPromise.then(([accessToken, resourceToken]) => ({
    headers: {
      ...headers,
      Authorization: accessToken ? `bearer ${accessToken}` : '',
      'X-SGED-Resource-Authorization': resourceToken,
    },
  }));
});

const errorLink = onError(({ response, networkError, graphQLErrors }): void => {
  if (graphQLErrors) {
    response.errors = null; // ignore errors because we redirect to error page

    for (const { extensions: { code } = {} } of graphQLErrors) {
      if (code === 'INTERNAL_SERVER_ERROR') {
        window.location = 'https://proc-error.sonyged.com/500.html';
        return;
      }
      if (code === 'NOT_FOUND') {
        window.location = 'https://proc-error.sonyged.com/404.html';
        return;
      }
    }

    window.location = 'https://proc-error.sonyged.com/408.html';
    return;
  }

  if (networkError) {
    if (500 <= networkError.statusCode && networkError.statusCode <= 599) {
      response.errors = null; // ignore errors because we redirect to error page
      window.location = 'https://proc-error.sonyged.com/500.html';
      return;
    }
  }
});

const httpLink = createHttpLink({
  uri: API_ENDPOINT,
});

export default ApolloLink.from([errorLink, authLink, httpLink]);
